import firebase from '../config/firebase';
import shipperService from './shipper.service';
import { calcularSelecionarTodosGeralCoerente } from '../components/permission';

const db = firebase.firestore();

const mapUserDocToSeller = (doc) => ({
    id: doc.id,
    name: doc.data().name,
    isSeller: Boolean(doc.data().isSeller),
    viewALL: doc.data().viewALL === true,
    uidShipper: doc.data().uidShipper || "",
    uidShipperAffiliates: Array.isArray(doc.data().uidShipperAffiliates)
        ? doc.data().uidShipperAffiliates
        : [],
    email: doc.data().email || "",
    phoneNumber: doc.data().phoneNumber || "",
    phoneNumberFirst: doc.data().phoneNumberFirst || "",
    phone: doc.data().phone || "",
    telefone: doc.data().telefone || "",
    photoURL: doc.data().photoURL || "",
    photoUrl: doc.data().photoUrl || "",
    file: doc.data().file || "",
    permissoes: doc.data().permissoes || {},
});

const sortUsersByName = (users) =>
    [...users].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "pt-BR")
    );

const applyViewOnlyPermissions = (permissoes = {}) => {
    const updated = {};

    Object.entries(permissoes).forEach(([key, group]) => {
        if (!group || typeof group !== "object") {
            updated[key] = group;
            return;
        }

        const hadAny = Boolean(
            group.selecionarTodos ||
                group.isView ||
                group.isCreate ||
                group.isEdit ||
                group.isDelete
        );

        updated[key] = hadAny
            ? {
                  selecionarTodos: false,
                  isView: true,
                  isCreate: false,
                  isEdit: false,
                  isDelete: false,
              }
            : {
                  selecionarTodos: false,
                  isView: false,
                  isCreate: false,
                  isEdit: false,
                  isDelete: false,
              };
    });

    return updated;
};

const userService = {

    async getUsers() {
        try {
            const snapshot = await db.collection('user_web').get();
            const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            return users;
        }
        catch (error) {
            console.error("Error getting documents: ", error);
            throw error;
        }
    },

    async updateUser(userId, userData) {
        try {
            await db.collection('user_web').doc(userId).update(userData);
        }
        catch (error) {
            console.error("Error updating document: ", error);
            throw error;
        }
    },

    async updateUserPerfil(perfil, permissions, selecionarTodosGeral) {
        try {
            const snapshot = await db.collection('user_web')
                .where('perfil', '==', perfil)
                .get();

            if (snapshot.empty) {
                console.log("Nenhum usuário encontrado com esse perfil.");
                return;
            }

            // Usa batch para fazer tudo de uma vez (melhor performance)
            const batch = db.batch();

            snapshot.docs.forEach(doc => {
                const userRef = db.collection('user_web').doc(doc.id);
                batch.update(userRef, { permissoes: permissions, permissoesAll: selecionarTodosGeral });
            });

            await batch.commit();
            console.log("Permissões atualizadas com sucesso para todos os usuários do perfil:", perfil);
        } catch (error) {
            console.error("Erro ao atualizar documentos:", error);
            throw error;
        }
    },


    getShipperIdsFromUser(user) {
        const affiliateUids = Array.isArray(user?.uidShipperAffiliates)
            ? user.uidShipperAffiliates
            : [];
        const primaryUid = user?.uidShipper ? [user.uidShipper] : [];
        return Array.from(new Set([...affiliateUids, ...primaryUid])).filter(Boolean);
    },

    /**
     * Embarcadores que têm a transportadora em uidShipperAffiliates
     * (usuários cujo affiliate aponta para o carrierId).
     */
    async getEmbarcadorIdsThatAffiliateCarrier(carrierId) {
        const carrierUid = String(carrierId || "").trim();
        if (!carrierUid) return [];

        try {
            const snapshot = await db
                .collection("user_web")
                .where("uidShipperAffiliates", "array-contains", carrierUid)
                .get();

            const embarcadorIds = snapshot.docs
                .map((doc) => {
                    const data = doc.data() || {};
                    return String(data.uidShipper || "").trim();
                })
                .filter((uid) => uid && uid !== carrierUid);

            return Array.from(new Set(embarcadorIds));
        } catch (error) {
            console.error(
                "Error getting embarcadores that affiliate carrier: ",
                error,
            );
            throw error;
        }
    },

    /**
     * Resolve o e-mail de pagamento configurado no cadastro do embarcador.
     */
    async getPaymentEmailByShipperId(shipperId) {
        if (!shipperId) return null;

        try {
            const shipperDoc = await db.collection("shipper").doc(shipperId).get();
            if (!shipperDoc.exists) return null;

            const paymentEmailUserId = shipperDoc.data()?.financial?.paymentEmailUser;
            if (!paymentEmailUserId) return null;

            const userDoc = await db.collection("user_web").doc(paymentEmailUserId).get();
            if (!userDoc.exists) return null;

            const email = String(userDoc.data()?.email || "").trim();
            return email || null;
        } catch (error) {
            console.error("Error resolving payment email by shipper id: ", error);
            throw error;
        }
    },

    /**
     * Retorna usuários vinculados aos embarcadores informados.
     */
    async getUsersByShipperIds(shipperIds) {
        const uids = Array.from(new Set((shipperIds || []).filter(Boolean)));
        if (uids.length === 0) return [];

        const CHUNK_SIZE = 10;
        const chunks = [];
        for (let i = 0; i < uids.length; i += CHUNK_SIZE) {
            chunks.push(uids.slice(i, i + CHUNK_SIZE));
        }

        try {
            const snapshots = await Promise.all(
                chunks.map((chunk) =>
                    db.collection("user_web")
                        .where("uidShipper", "in", chunk)
                        .get()
                )
            );

            const seen = new Set();
            const users = [];

            snapshots.forEach((snapshot) => {
                snapshot.docs.forEach((doc) => {
                    if (seen.has(doc.id)) return;
                    seen.add(doc.id);
                    users.push(mapUserDocToSeller(doc));
                });
            });

            return users.sort((a, b) =>
                String(a.name || "").localeCompare(String(b.name || ""), "pt-BR")
            );
        } catch (error) {
            console.error("Error getting users by shipper ids: ", error);
            throw error;
        }
    },

    /**
     * Retorna vendedores (isSeller) vinculados aos embarcadores informados.
     */
    async getSellersByShipperIds(shipperIds) {
        const uids = Array.from(new Set((shipperIds || []).filter(Boolean)));
        if (uids.length === 0) return [];

        const CHUNK_SIZE = 10;
        const chunks = [];
        for (let i = 0; i < uids.length; i += CHUNK_SIZE) {
            chunks.push(uids.slice(i, i + CHUNK_SIZE));
        }

        try {
            const snapshots = await Promise.all(
                chunks.map((chunk) =>
                    db.collection("user_web")
                        .where("isSeller", "==", true)
                        .where("uidShipper", "in", chunk)
                        .get()
                )
            );

            const seen = new Set();
            const sellers = [];

            snapshots.forEach((snapshot) => {
                snapshot.docs.forEach((doc) => {
                    if (seen.has(doc.id)) return;
                    seen.add(doc.id);
                    sellers.push(mapUserDocToSeller(doc));
                });
            });

            return sellers.sort((a, b) =>
                String(a.name || "").localeCompare(String(b.name || ""), "pt-BR")
            );
        } catch (error) {
            console.error("Error getting sellers by shipper ids: ", error);
            throw error;
        }
    },

    /**
     * Usuários vinculados ao embarcador e opções para acesso de visualização.
     */
    async getShipperUserManagementData(shipperId, loggedUser) {
        if (!shipperId) {
            return {
                linkedUsers: [],
                accessOptions: [],
                selectedAccessUserIds: [],
            };
        }

        try {
            const linkedSnapshot = await db
                .collection("user_web")
                .where("uidShipper", "==", shipperId)
                .get();

            const linkedUsers = sortUsersByName(
                linkedSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    name: doc.data().name,
                    email: doc.data().email || "",
                }))
            );

            const linkedIds = new Set(linkedUsers.map((userItem) => userItem.id));
            const scopeShipperIds = this.getShipperIdsFromUser(loggedUser);
            const scopeUsers = await this.getUsersByShipperIds(scopeShipperIds);

            const affiliateSnapshot = await db
                .collection("user_web")
                .where("uidShipperAffiliates", "array-contains", shipperId)
                .get();

            const accessOptionsMap = new Map();
            const selectedAccessUserIds = new Set();

            scopeUsers
                .filter(
                    (userItem) =>
                        userItem.uidShipper !== shipperId &&
                        userItem.viewALL !== true,
                )
                .forEach((userItem) => {
                    accessOptionsMap.set(userItem.id, {
                        id: userItem.id,
                        name: userItem.name,
                        email: userItem.email || "",
                    });

                    if ((userItem.uidShipperAffiliates || []).includes(shipperId)) {
                        selectedAccessUserIds.add(userItem.id);
                    }
                });

            affiliateSnapshot.docs.forEach((doc) => {
                const data = doc.data() || {};
                if (linkedIds.has(doc.id)) return;
                if (data.uidShipper === shipperId) return;
                // viewALL vê tudo pela permissão; não entra em "Usuário com Acesso"
                if (data.viewALL === true) return;

                accessOptionsMap.set(doc.id, {
                    id: doc.id,
                    name: data.name,
                    email: data.email || "",
                });
                selectedAccessUserIds.add(doc.id);
            });

            return {
                linkedUsers,
                accessOptions: sortUsersByName(
                    Array.from(accessOptionsMap.values())
                ),
                selectedAccessUserIds: Array.from(selectedAccessUserIds),
            };
        } catch (error) {
            console.error("Error getting shipper user management data: ", error);
            throw error;
        }
    },

    async appendShipperAffiliateForUser(userId, shipperId) {
        if (!userId || !shipperId) {
            throw new Error("Usuário ou embarcador inválido.");
        }

        const userDoc = await db.collection("user_web").doc(userId).get();
        if (!userDoc.exists) {
            throw new Error("Usuário não encontrado.");
        }

        const userData = userDoc.data();
        const currentAffiliates = Array.isArray(userData.uidShipperAffiliates)
            ? userData.uidShipperAffiliates
            : [];
        const nextAffiliates = Array.from(new Set([...currentAffiliates, shipperId]));

        if (
            nextAffiliates.length === currentAffiliates.length &&
            currentAffiliates.includes(shipperId)
        ) {
            return nextAffiliates;
        }

        await db.collection("user_web").doc(userId).update({
            uidShipperAffiliates: nextAffiliates,
        });

        return nextAffiliates;
    },

    async appendShipperAffiliateForAuthUser(authUid, shipperId) {
        if (!authUid) {
            throw new Error("Usuário autenticado inválido.");
        }

        const snapshot = await db
            .collection("user_web")
            .where("uid", "==", authUid)
            .limit(1)
            .get();

        if (snapshot.empty) {
            throw new Error("Usuário não encontrado.");
        }

        return this.appendShipperAffiliateForUser(snapshot.docs[0].id, shipperId);
    },

    async grantShipperAccessForUser(userId, shipperId) {
        if (!userId || !shipperId) {
            throw new Error("Usuário ou embarcador inválido para conceder acesso.");
        }

        const userDoc = await db.collection("user_web").doc(userId).get();
        if (!userDoc.exists) {
            throw new Error("Usuário não encontrado.");
        }

        const userData = userDoc.data();
        const currentAffiliates = Array.isArray(userData.uidShipperAffiliates)
            ? [...userData.uidShipperAffiliates]
            : [];
        const nextAffiliates = Array.from(new Set([...currentAffiliates, shipperId]));
        const permissoes = applyViewOnlyPermissions(userData.permissoes || {});
        const permissoesAll = calcularSelecionarTodosGeralCoerente(permissoes);

        await db.collection("user_web").doc(userId).update({
            uidShipperAffiliates: nextAffiliates,
            permissoes,
            permissoesAll,
        });
    },

    async revokeShipperAccessForUser(userId, shipperId) {
        if (!userId || !shipperId) {
            throw new Error("Usuário ou embarcador inválido para retirar acesso.");
        }

        const userDoc = await db.collection("user_web").doc(userId).get();
        if (!userDoc.exists) {
            throw new Error("Usuário não encontrado.");
        }

        const userData = userDoc.data();
        const currentAffiliates = Array.isArray(userData.uidShipperAffiliates)
            ? userData.uidShipperAffiliates
            : [];
        const nextAffiliates = currentAffiliates.filter(
            (affiliateId) => affiliateId !== shipperId
        );

        await db.collection("user_web").doc(userId).update({
            uidShipperAffiliates: nextAffiliates,
        });
    },

    async syncShipperAccessUsers(shipperId, nextSelectedUserIds, previousSelectedUserIds) {
        const previousSet = new Set(previousSelectedUserIds || []);
        const nextSet = new Set(nextSelectedUserIds || []);

        const toGrant = [...nextSet].filter((userId) => !previousSet.has(userId));
        const toRevoke = [...previousSet].filter((userId) => !nextSet.has(userId));

        await Promise.all([
            ...toGrant.map((userId) => this.grantShipperAccessForUser(userId, shipperId)),
            ...toRevoke.map((userId) => this.revokeShipperAccessForUser(userId, shipperId)),
        ]);
    },

    /**
     * @deprecated Use grantShipperAccessForUser ou syncShipperAccessUsers.
     */
    async updateShipperAccessForUser(userId, shipperId, grant) {
        if (grant) {
            await this.grantShipperAccessForUser(userId, shipperId);
            return;
        }

        await this.revokeShipperAccessForUser(userId, shipperId);
    },

    /**
     * Retorna uma lista de usuários associados com o Fortio.
     * 
     * @param {string} userId - ID do usuário atual
     * @returns {object[]} - uma lista de objetos que contém o ID e o nome dos usuários associados com o Fortio
     * @throws {Error} - lança um erro se ocorrer algum problema durante a execução da função
     */
    async usersAssociatedWithFortio() {
        try {

            const result = await db.collection("user_web")
            // .where("uidShipper", "==", resultShipper.id)
            .get();
            
            const list = result.docs.map(mapUserDocToSeller);

            return list;
        }
        catch (error) {
            console.error("Error deleting document: ", error);
            throw error;
        }
    }
}

export default userService;